import { useEffect, useRef, useState, useSyncExternalStore, type ButtonHTMLAttributes, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Loader2, X } from "lucide-react";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "ghost" | "danger";
export function Button({ variant = "secondary", busy, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  const styles: Record<Variant, string> = {
    primary: "bg-accent text-accent-ink hover:bg-accent-hover border-transparent",
    secondary: "bg-surface text-ink-2 hover:bg-surface-2 border-line",
    ghost: "bg-transparent text-ink-2 hover:bg-surface-2 border-transparent",
    danger: "bg-bad text-white hover:opacity-90 border-transparent",
  };
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={cx("inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 h-9 text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed", styles[variant], className)}
    >
      {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-muted text-sm py-10 justify-center" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden /> {label}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-bad-soft text-bad px-3 py-2 text-sm" role="alert">
      <AlertTriangle className="size-4 mt-0.5 shrink-0" aria-hidden /> <div>{children}</div>
    </div>
  );
}

export function Empty({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center py-16 px-4 text-muted">
      <div className="mb-3 text-muted">{icon}</div>
      <div className="text-ink font-medium">{title}</div>
      {children && <div className="text-sm mt-1 max-w-sm">{children}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input, select, textarea, button:not([data-close])");
    first?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-scrim p-0 sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className={cx("bg-surface w-full rounded-t-2xl sm:rounded-2xl shadow-xl border border-line max-h-[90vh] flex flex-col", wide ? "sm:max-w-2xl" : "sm:max-w-md")}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <button data-close onClick={onClose} className="p-1 rounded-md text-muted hover:bg-surface-2" aria-label="Close">
            <X className="size-5" />
          </button>
        </div>
        <div className="px-5 py-2 overflow-y-auto">{children}</div>
        {footer && <div className="flex justify-end gap-2 px-5 py-4">{footer}</div>}
      </div>
    </div>
  );
}

// ---------- toasts ----------
type Toast = { id: number; text: string; kind: "ok" | "error" };
let toasts: Toast[] = [];
const toastListeners = new Set<() => void>();
const emitToasts = () => toastListeners.forEach((l) => l());
export function toast(text: string, kind: Toast["kind"] = "ok") {
  const id = Date.now() + Math.random();
  toasts = [...toasts, { id, text, kind }];
  emitToasts();
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emitToasts();
  }, kind === "error" ? 8000 : 4000);
}
export function ToastHost() {
  const list = useSyncExternalStore(
    (l) => {
      toastListeners.add(l);
      return () => toastListeners.delete(l);
    },
    () => toasts,
  );
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-md" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={cx("flex items-start gap-2 rounded-lg px-4 py-3 text-sm shadow-lg border", t.kind === "error" ? "bg-bad-soft text-bad border-bad/30" : "bg-surface text-ink border-line")}>
          {t.kind === "error" ? <AlertTriangle className="size-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="size-4 mt-0.5 shrink-0 text-good" />}
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

/** A text prompt dialog (new folder, rename). */
export function PromptDialog({ title, label, initial = "", confirm, validate, onSubmit, onClose }: { title: string; label: string; initial?: string; confirm: string; validate?: (v: string) => string | null; onSubmit: (v: string) => Promise<void>; onClose: () => void }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Select the name without its extension, like a desktop file manager.
    const i = initial.lastIndexOf(".");
    inputRef.current?.setSelectionRange(0, i > 0 ? i : initial.length);
  }, [initial]);
  const submit = async () => {
    const problem = validate?.(value) ?? null;
    if (problem) return setError(problem);
    setBusy(true);
    try {
      await onSubmit(value);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={submit}>
            {confirm}
          </Button>
        </>
      }
    >
      <label className="block text-sm font-medium text-ink-2 mb-1">{label}</label>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => (setValue(e.target.value), setError(null))}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        className="w-full h-10 rounded-lg border border-line bg-surface px-3 text-sm text-ink"
      />
      {error && <p className="text-sm text-bad mt-2">{error}</p>}
    </Modal>
  );
}

export function ConfirmDialog({ title, children, confirm, danger, onConfirm, onClose }: { title: string; children: ReactNode; confirm: string; danger?: boolean; onConfirm: () => Promise<void> | void; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={danger ? "danger" : "primary"}
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                onClose();
              } catch (e) {
                setError((e as Error).message);
                setBusy(false);
              }
            }}
          >
            {confirm}
          </Button>
        </>
      }
    >
      <div className="text-sm text-ink-2 space-y-2">{children}</div>
      {error && <p className="text-sm text-bad mt-2">{error}</p>}
    </Modal>
  );
}
